/* Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useState
} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {globalGroupAccountHierarchySelector} from '@ion/applications/src/store/selectors/global-group';
import {getAccountLineage, setAccountTree} from '@ion/core/src/entities/Accounts';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Account, AccountLink} from '@ion/core/src/types/entities';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {useAccountsSearch} from '@ion/query/src/hooks/useAccountsSearch';
import Dropdown, {
  createOptions,
  Options
} from '../components/menus/Dropdown';
import {dlsColor, dlsTypography} from '../theme/theme';

// defines the Searchable Account Dropdown prop definitions ...
export type SearchableAccountDropdownProps = {
  loggedInAccount: AccountLink;
  onChange: Function;
  readOnly?: boolean;
  required?: boolean;
  loading?: boolean;
  account?: Account;
  hasError?: boolean;
  errorText?: string;
  isInfiniteScroll?: boolean;
};

// default styles used by the component when a classes prop is not provided ...
const useStyles = makeStyles((theme) => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  },
  actionLabel : {
    minWidth : '120px'
  },
  boxStyle : {
    marginTop : '14px',
    fontSize  : 'xx-large'
  },
  boxfieldLabel : {
    color        : dlsColor('grey2'),
    marginBottom : '8px',
    ...dlsTypography('body3')
  },
  customPaperTitle : {
    padding                        : '28px 46px 0 46px !important',
    textTransform                  : 'uppercase',
    fontWeight                     : 'bold',
    fontSize                       : '14px',
    fontFamily                     : 'Roboto',
    color                          : dlsColor('grey4'),
    boxShadow                      : '0px 0px 0px 0px rgba(0,0,0,0.2), 0px 2px 1px 0px rgba(0,0,0,0.14), 0px 2px 3px 0px rgba(0,0,0,0.12)',
    [theme.breakpoints.down('sm')] : {
      '&&' : {
        padding : '26px 20px 0 20px !important'
      }
    }
  },
  customPaperTitleCTC : {
    fontWeight : 'bold',
    fontSize   : '14px',
    fontFamily : 'Roboto',
    color      : dlsColor('grey4')
  },
  tooltipText : {
    fontSize : '14px'
  }
}));

const getOptions = (accountOptions): Options[] => {
  return createOptions({
    items     : accountOptions,
    valProp   : 'id',
    labelProp : 'name',
    augmentor : (item: Account, option: { value: string; label: string; }) => {
      return {...option, title: item.name, id: item?.id};
    }
  });
};

const getTooltipText = (parentHierarchy: string[]): string => {
  return parentHierarchy?.length && parentHierarchy.reverse().join(' → ');
};

const onRenderAccountNameOption = (accountTree, classes) => {
  return (option): JSX.Element => {
    const parentHierarchy = getAccountLineage(option.value, accountTree);
    const immediateParent = parentHierarchy[1];
    const optionlabel     = immediateParent ? `${option.label}(${immediateParent})` : `${option.label}`;
    return (
      <Tooltip title={<span className={classes.tooltipText}>{getTooltipText(parentHierarchy)}</span>}
        placement={'top'}
        enterDelay={500}
        arrow>
        <span>{optionlabel}</span>
      </Tooltip>
    );
  };
};

const MIN_INPUT_LENGTH = 0;
const MAX_INPUT_LENGTH = 3;

/**
 * The SearchableAccountDropdown provides a list of accounts for the selected account or
 * user's default account for display within a dropdown list and
 * allows the user to select an Account from the list which will be returned to the
 * client as the selected Account.
 */
export const SearchableAccountDropdown = ({
  onChange,
  required,
  loading,
  loggedInAccount,
  errorText,
  hasError = false,
  isInfiniteScroll = true
}: SearchableAccountDropdownProps) => {
  const classes                                = useStyles();
  const [isLoading, setIsLoading]               = useState<boolean>(loading);
  const [selected, setSelected]                 = useState<string>();
  const [options, setOptions]                   = useState<Options[]>([]);
  const [pageId, setPageId]                     = useState<number>(1);
  const [isScroll, setIsScroll]                 = useState<boolean>(false);
  const [isSearch, setIsSearch]                 = useState<boolean>(false);
  const [searchTerms, setSearchTerms]           = useState<string>();
  const [isCleared, setIsCleared]               = useState<boolean>(false);
  const [scrolledAccounts, setScrolledAccounts] = useState([]);
  const {loadAccounts}                          = useAccountsSearch();
  const accountId                               = entityId(loggedInAccount);
  const accountHierarchy                        = useSelector(globalGroupAccountHierarchySelector);
  const accountTree                             = setAccountTree(accountHierarchy, true);

  useEffect(() => {
    if ((isInfiniteScroll && isScroll) || (isInfiniteScroll && (searchTerms?.length > MAX_INPUT_LENGTH))) {
      handleAccountList();
    }
  }, [pageId, searchTerms, isScroll, isSearch, isCleared, isInfiniteScroll, accountId]);

  useEffect(() => {
    if (isInfiniteScroll && isScroll && !isCleared) {
      setOptions(getOptions(scrolledAccounts));
    }
  }, [isInfiniteScroll, scrolledAccounts, isCleared]);

  const handleAccountList = useCallback(() => {
    setIsLoading(true);
    try {
      loadAccounts({
        pageSize    : 10,
        pageId      : pageId,
        searchTerms : searchTerms,
        accountId
      }, {
        onSuccess : (searchedAccounts) => {
          setScrolledAccounts(scrolledAccounts?.concat(searchedAccounts));
          setIsLoading(false);
          if (isSearch) {
            setOptions(getOptions(searchedAccounts));
          }
        },
        onError : () => {
          setOptions([]);
        }
      });
    } catch (err) {
      setOptions([]);
    }
  }, [pageId, searchTerms, isScroll, isSearch, isCleared, accountId, isInfiniteScroll, options]);

  const handleChange = useCallback(function(event: React.ChangeEvent, child) {
    const {title} = child || {};
    setSelected(title);
    onChange?.(child);
    setSearchTerms(title);
    if (!title) {
      setIsLoading(false);
      setIsSearch(false);
      setIsCleared(true);
      setScrolledAccounts([]);
      setPageId(1);
      setOptions(getOptions([]));
    }
    if (isInfiniteScroll) {
      handleAccountClickOutside();
    }
  }, [selected, scrolledAccounts]);

  const onInfiniteScroll = useCallback((scrollHeight, scrollTop, clientHeight) => {
    if ((scrollTop + clientHeight >= scrollHeight - 2)) {
      setPageId((prevActiveStep) => prevActiveStep + 1);
      setIsScroll(true);
      setIsSearch(false);
      setIsCleared(false);
    }
  }, []);

  const onSearchAccounts = useCallback((value: string) => {
    if (value?.length > MAX_INPUT_LENGTH) {
      setSearchTerms(value);
      setIsSearch(true);
      setPageId(1);
    } else if (value?.length === MIN_INPUT_LENGTH) {
      setPageId(1);
      handleAccountClickOutside();
    }
  }, []);

  const handleAccountClickOutside = useCallback(() => {
    setSearchTerms('');
    setIsLoading(false);
    setIsSearch(false);
    setIsCleared(true);
    setOptions(getOptions([]));
    setScrolledAccounts([]);
  }, []);

  const noOptionsText = useCallback((): ReactNode => {
    const noResults = !(searchTerms?.length > MAX_INPUT_LENGTH) && !options.length;
    return (
      noResults ? (
        <div className={classes.customPaperTitleCTC}>
          {translate('common:search.startTyping')}
        </div>)
        : null
    );
  }, [classes, searchTerms, options]);

  return (
    <div className={classes.root}>
      <Dropdown required={required}
        enableClear
        displaySelectedLabel
        hasError={hasError}
        errorText={errorText}
        options={options}
        loading={isLoading}
        noOptionsText={noOptionsText()}
        renderOption={onRenderAccountNameOption(accountTree, classes)}
        onInfiniteScroll={onInfiniteScroll}
        onSearchAssociations={onSearchAccounts}
        onHandleClickOutside={handleAccountClickOutside}
        isInfiniteScroll={isInfiniteScroll}
        value={isLoading ? translate('common:states.loading') : selected}
        fieldLabel={translate('common:editors.bulkActions.dialogBulkAction.account')}
        placeholder={translate('common:editors.shared.selectGroup')}
        onChange={handleChange} />
    </div>
  );
};

export default SearchableAccountDropdown;
